import React, { useEffect, useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbButton from '../security-toolbox-ui/StbButton';
import StbModal from '../security-toolbox-ui/StbModal';
import { useLabConfigurations, useLabSessions, useSaveLabRow } from '../../hooks/lab-hooks';
import { toDateTimeLocalValue } from '../../support/dcloudService';
import {
  LabConfiguration,
  LabEvent,
  LabEventMode,
  LabOption,
  labConfigurationSummary,
  labSlugify,
  LAB_EVENT_MODES,
  LAB_EVENT_STATES,
  LAB_TABLES,
} from '../../support/labService';
import { LAB_JSON_SCHEMAS } from '../../support/labSpecSchema';
import {
  LabCheckbox,
  LabDerivedKey,
  LabFieldRow,
  LabOptionSelect,
  labErrorMessage,
} from './LabShared';
import LabConfigurationEditorModal from './LabConfigurationEditorModal';
import { LabSpecBuilder } from './LabSpecFields';

export type LabEventEditorModalProps = {
  open: boolean;
  event: LabEvent | null;
  /** Seeds the configuration for a new event, for "deliver this" from a configuration row. */
  defaultConfigurationId?: number;
  onClose: () => void;
  onSaved?: (event: LabEvent) => void;
};

const EMPTY: Partial<LabEvent> = {
  name: '',
  slug: '',
  description: '',
  mode: 'pool',
  state: 'draft',
  starts_at: null,
  ends_at: null,
  seat_count: 0,
  session: null,
  owner_email: '',
  contact_email: '',
  auto_start_run: true,
  metadata: {},
};

/** A `datetime-local` value back to something the API will store, or null when cleared. */
const fromDateTimeLocal = (value: string): string | null => {
  if (!value) return null;

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

/**
 * Schedule an occasion on which a published lab is delivered.
 *
 * An event is the pairing of *which published lab* with *when and for whom*. It does not apply
 * the overlay: the scenario and its steps land on the pod once the session goes active, which is
 * why an event can be edited or cancelled while pods are running without touching one.
 *
 * The two modes differ only in how a pod is found - one session named directly, or a pool that
 * hands them out - so the fields that only apply to one are hidden rather than left to be filled
 * in wrongly. Neither pairing is enforced by the database, deliberately: a draft event with
 * neither is a normal thing to save halfway through.
 */
const LabEventEditorModal: React.FC<LabEventEditorModalProps> = ({
  open,
  event,
  defaultConfigurationId,
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<Partial<LabEvent>>(EMPTY);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [newConfiguration, setNewConfiguration] = useState(false);
  /**
   * A publication created from inside this form, held until the catalogue query catches up.
   *
   * The summary below and the demo-id mismatch check both read the publication row rather than the
   * id, so without this an event scheduled straight after creating one would show "no publication"
   * over a publication that is chosen and real.
   */
  const [createdConfiguration, setCreatedConfiguration] = useState<LabConfiguration | null>(null);

  const save = useSaveLabRow<Partial<LabEvent> & { id?: number }>(LAB_TABLES.event);

  const { data: configurations } = useLabConfigurations();
  // Only needed in single mode, and only worth the request while the modal is open.
  const { data: sessions } = useLabSessions(false, {
    enabled: open && draft.mode === 'single',
  });

  useEffect(() => {
    if (!open) return;

    setDraft(
      event
        ? { ...event }
        : {
            ...EMPTY,
            ...(defaultConfigurationId ? { configuration: defaultConfigurationId } : {}),
          },
    );
    setJsonError(null);
    setFormError(null);
    setNewConfiguration(false);
    setCreatedConfiguration(null);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event?.id, defaultConfigurationId]);

  const set = (patch: Partial<LabEvent>) => setDraft((current) => ({ ...current, ...patch }));

  const selectedConfiguration = useMemo(
    () =>
      (configurations || []).find((entry) => entry.id === draft.configuration) ||
      (createdConfiguration && createdConfiguration.id === draft.configuration
        ? createdConfiguration
        : null),
    [configurations, createdConfiguration, draft.configuration],
  );

  /**
   * Derived from the name, and only for a new event.
   *
   * Same rule as the lab and scenario forms: the slug is what other rows record, so it is shown
   * rather than typed, and an existing event keeps the one it was saved with. Two events named
   * the same collide on the unique constraint, which the API says plainly - and two events named
   * the same is worth being told about anyway.
   */
  const generatedSlug = useMemo(
    () => (event ? event.slug : labSlugify(draft.name || '')),
    [event, draft.name],
  );

  const configurationOptions: LabOption<string>[] = useMemo(() => {
    const rows = [...(configurations || [])];

    if (createdConfiguration && !rows.some((row) => row.id === createdConfiguration.id)) {
      rows.push(createdConfiguration);
    }

    return rows.map((configuration) => ({
      value: String(configuration.id),
      label: configuration.name,
      hint: labConfigurationSummary(configuration),
    }));
  }, [configurations, createdConfiguration]);

  const sessionOptions: LabOption<string>[] = useMemo(
    () =>
      (sessions || []).map((session) => ({
        value: String(session.id),
        label: `${session.name || session.session_key} (${session.state})`,
        hint: [session.owner_email, session.demo_name].filter(Boolean).join(' • ') || undefined,
      })),
    [sessions],
  );

  const chosenSession = useMemo(
    () => (sessions || []).find((session) => session.id === draft.session) || null,
    [sessions, draft.session],
  );

  /**
   * A session launched from a different demo than the configuration names is the failure that
   * looks like nothing: the pod comes up, the overlay does not apply, and nobody can say why.
   */
  const demoMismatch =
    !!chosenSession &&
    !!selectedConfiguration?.demo_uid &&
    !!chosenSession.demo_uid &&
    chosenSession.demo_uid !== selectedConfiguration.demo_uid;

  const handleSubmit = async () => {
    if (!draft.configuration) {
      setFormError('Pick the published lab this delivers. That is what decides the data centre and the pod.');
      return;
    }

    if (!draft.name?.trim()) {
      setFormError('This needs a name.');
      return;
    }

    if (!generatedSlug) {
      setFormError(
        'No slug could be derived from that name. Use letters or numbers somewhere in it.',
      );
      return;
    }

    // Compared as instants, not as strings: what the API returns and what this form writes are
    // both ISO but not the same ISO, so a text comparison is not reliably right.
    if (
      draft.starts_at &&
      draft.ends_at &&
      new Date(draft.ends_at).getTime() <= new Date(draft.starts_at).getTime()
    ) {
      setFormError('The event ends before it starts.');
      return;
    }

    // Only refused once somebody says it is more than a draft: an unfinished draft is the
    // normal state of a half-planned event.
    if (draft.state !== 'draft' && draft.mode === 'single' && !draft.session) {
      setFormError(
        'A one-session event past draft needs its session. Nothing comes up without it - leave it as a draft until the session exists.',
      );
      return;
    }

    if (jsonError) {
      setFormError(`Metadata: ${jsonError}`);
      return;
    }

    setFormError(null);

    try {
      // The fields belonging to the other mode are cleared rather than carried: a pool event
      // still holding a session is an event two things could deliver.
      const payload: Partial<LabEvent> =
        draft.mode === 'single'
          ? { ...draft, slug: generatedSlug, seat_count: 0 }
          : { ...draft, slug: generatedSlug, session: null };

      const saved = await save.mutateAsync(payload);
      onSaved?.(saved as LabEvent);
      onClose();
    } catch {
      // Surfaced through save.error.
    }
  };

  const submitError = formError || (save.isError ? labErrorMessage(save.error) : null);

  return (
    <StbModal
      open={open}
      role="update"
      title={event ? `Edit ${event.name}` : 'Schedule a lab event'}
      size="large"
      scrollable
      submitLabel={event ? 'Save event' : 'Create event'}
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        submitError ? <p className="m-0 text-sm text-amber-800">{submitError}</p> : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <LabOptionSelect
            label="Published lab"
            value={draft.configuration ? String(draft.configuration) : ''}
            options={configurationOptions}
            allowEmpty
            emptyLabel="Pick a lab"
            onChange={(value) => set({ configuration: value ? Number(value) : undefined })}
            helpText="The data centre, dCloud lab id, template and scenario all come from this one choice."
          />
          <div>
            <StbButton role="add" size="xsmall" onClick={async () => setNewConfiguration(true)}>
              + New published lab
            </StbButton>
          </div>
        </div>

        {selectedConfiguration && (
          <div className="rounded border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
            <div>{labConfigurationSummary(selectedConfiguration)}</div>
            <div className="mt-1">
              {`demo ${selectedConfiguration.demo_uid ?? 'not set'}`}
              {selectedConfiguration.template_topology_uid
                ? ` • template ${selectedConfiguration.template_topology_uid}`
                : ' • no template recorded'}
            </div>
          </div>
        )}

        <TextField
          label="Event name"
          value={draft.name || ''}
          onChange={(evt) => set({ name: evt.target.value })}
          helperText="Which occasion this is - the cohort, the customer, the week. The slug follows from it."
          fullWidth
        />

        <LabDerivedKey
          value={generatedSlug}
          frozen={!!event}
          label="Slug"
          usage="Runs and reports record the event by it."
        />

        <TextField
          label="Description"
          value={draft.description || ''}
          onChange={(evt) => set({ description: evt.target.value })}
          multiline
          minRows={2}
          fullWidth
        />

        <LabFieldRow>
          <LabOptionSelect
            label="Mode"
            value={draft.mode as LabEventMode}
            options={LAB_EVENT_MODES}
            onChange={(mode) => set({ mode })}
          />
          <LabOptionSelect
            label="State"
            value={draft.state}
            options={LAB_EVENT_STATES}
            onChange={(state) => set({ state })}
          />
        </LabFieldRow>

        <LabFieldRow>
          <TextField
            label="Starts"
            type="datetime-local"
            value={toDateTimeLocalValue(draft.starts_at)}
            onChange={(evt) => set({ starts_at: fromDateTimeLocal(evt.target.value) })}
            InputLabelProps={{ shrink: true }}
            helperText="In your own time zone. Stored in UTC."
            fullWidth
          />
          <TextField
            label="Ends"
            type="datetime-local"
            value={toDateTimeLocalValue(draft.ends_at)}
            onChange={(evt) => set({ ends_at: fromDateTimeLocal(evt.target.value) })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </LabFieldRow>

        {draft.mode === 'pool' ? (
          <>
            <TextField
              label="Expected attendees"
              type="number"
              value={draft.seat_count ?? 0}
              onChange={(evt) => set({ seat_count: Number(evt.target.value) })}
              helperText="A target, not a limit. The pools decide how many pods there actually are; this is what they are measured against."
              fullWidth
            />
            <p className="m-0 text-xs text-gray-500">
              {event
                ? 'Pods are stocked and handed out by the pools on this event, managed under Pools.'
                : 'Save the event, then add a pool to it - that is where stocking and claiming live.'}
            </p>
          </>
        ) : (
          <>
            <LabOptionSelect
              label="Session"
              value={draft.session ? String(draft.session) : ''}
              options={sessionOptions}
              allowEmpty
              emptyLabel="Not chosen yet"
              onChange={(value) => set({ session: value ? Number(value) : null })}
              helpText="The pod this event runs on. Sessions appear here once something has observed them in dCloud."
            />

            {demoMismatch && (
              <p className="m-0 text-sm text-amber-800">
                {`That session was launched from demo ${chosenSession?.demo_uid}, but this configuration names demo ${selectedConfiguration?.demo_uid}. The pod will come up and the lab will not apply to it.`}
              </p>
            )}
          </>
        )}

        <LabFieldRow>
          <TextField
            label="Owner email"
            value={draft.owner_email || ''}
            onChange={(evt) => set({ owner_email: evt.target.value })}
            helperText="Who is running it."
            fullWidth
          />
          <TextField
            label="Contact email"
            value={draft.contact_email || ''}
            onChange={(evt) => set({ contact_email: evt.target.value })}
            helperText="Where questions about this event go, if not the owner."
            fullWidth
          />
        </LabFieldRow>

        <LabCheckbox
          label="Open a run as soon as a pod goes active"
          checked={!!draft.auto_start_run}
          onChange={(auto_start_run) => set({ auto_start_run })}
          helpText="Off means somebody starts each run by hand, and nothing is recorded until they do."
        />

        <LabSpecBuilder
          schema={LAB_JSON_SCHEMAS.eventMetadata}
          value={draft.metadata}
          resetKey={event?.id ?? 'new'}
          onChange={(metadata, error) => {
            setJsonError(error);
            if (!error) set({ metadata });
          }}
        />
      </div>

      {/* A lab published in a data centre nobody has published it in yet is the usual reason an
          event cannot be scheduled, and it is answerable here rather than three screens away. */}
      <LabConfigurationEditorModal
        open={newConfiguration}
        configuration={null}
        existing={configurations || []}
        onClose={() => setNewConfiguration(false)}
        onSaved={(saved) => {
          setCreatedConfiguration(saved);
          set({ configuration: saved.id });
        }}
      />
    </StbModal>
  );
};

export default LabEventEditorModal;
